import { useState } from "react";
import { ChevronRight, LayoutList, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { CabeceraDelMantenedor } from "@/components/plantillas/CabeceraDelMantenedor";
import { NuevaPlantillaDialog } from "@/components/plantillas/DialogosDePlantilla";
import { BarraDeComposicion, ConteoDeObligatoriedad } from "@/components/plantillas/etiquetas";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePlantillas } from "@/hooks/usePlantillas";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { respaldoDeLaPlantilla } from "@/lib/plantillas";
import { cn } from "@/lib/utils";

const FILAS_SKELETON = 4;

const ENCABEZADO = "text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.04em] uppercase";

/**
 * Mantenedor de plantillas de informe (listado).
 *
 * Cada plantilla define qué contenidos lleva un tipo de informe y en qué
 * orden; los contenidos salen del maestro de ítems (la otra vista, con su
 * interruptor arriba). El listado es una TABLA —filas equivalentes con las
 * mismas columnas— y se entra a una plantilla por su nombre.
 *
 * Bajo el nombre dice con cuántos informes reales se armó la plantilla
 * (`informes_analizados`); las que se crean en la app no tienen ese dato y la
 * línea no se dibuja.
 */
export function Contenidos() {
  const plantillas = usePlantillas();
  const estado = estadoDeConsulta(plantillas);
  const navigate = useNavigate();
  const [creando, setCreando] = useState(false);

  const lista = plantillas.data ?? [];

  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <CabeceraDelMantenedor vista="plantillas" />

      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <p className="text-ink-body max-w-[40rem] flex-1 text-sm font-medium text-pretty">
          Cada plantilla define qué contenidos lleva un tipo de informe y en qué orden. Los
          contenidos salen del maestro de ítems.
        </p>
        <Button variant="secondary" onClick={() => setCreando(true)}>
          <Plus className="h-4 w-4" />
          Nueva plantilla
        </Button>
      </div>

      <div className="space-y-5">
        {estado.sinConexion && <EstadoSinConexion que="las plantillas" />}

        {estado.hayError && (
          <EstadoDeError
            titulo="No se pudieron cargar las plantillas"
            error={plantillas.error}
            onReintentar={plantillas.refetch}
            reintentando={plantillas.isFetching}
          />
        )}

        {!estado.cargando && !estado.hayError && !estado.sinConexion && lista.length === 0 ? (
          <Empty className="rounded-xl border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <LayoutList />
              </EmptyMedia>
              <EmptyTitle>Todavía no hay plantillas</EmptyTitle>
              <EmptyDescription>
                Crea la primera para definir qué lleva cada tipo de informe.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="secondary" onClick={() => setCreando(true)}>
                <Plus className="h-4 w-4" />
                Nueva plantilla
              </Button>
            </EmptyContent>
          </Empty>
        ) : null}

        {estado.cargando || lista.length > 0 ? (
          <div className="bg-card overflow-hidden rounded-xl border">
            <Table>
              <TableHeader className="bg-superficie-sutil">
                <TableRow className="hover:bg-transparent">
                  <TableHead className={cn(ENCABEZADO, "px-5.5")}>Plantilla</TableHead>
                  <TableHead className={cn(ENCABEZADO, "w-[3.25rem] text-right")}>Ítems</TableHead>
                  <TableHead className={cn(ENCABEZADO, "w-[8.25rem]")}>Composición</TableHead>
                  <TableHead className="w-[3.5rem] pr-5" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {estado.cargando
                  ? Array.from({ length: FILAS_SKELETON }).map((_, i) => (
                      <TableRow key={i} aria-label="Cargando plantilla" className="hover:bg-transparent">
                        <TableCell className="px-5.5 py-4">
                          <Skeleton className="h-3.5 w-56" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="ml-auto h-3.5 w-8" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3.5 w-32" />
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    ))
                  : lista.map((plantilla) => (
                      <TableRow
                        key={plantilla.id}
                        className="hover:bg-superficie-sutil cursor-pointer"
                        onClick={() => navigate(`/admin/contenidos/plantillas/${plantilla.id}`)}
                      >
                        <TableCell className="w-full max-w-0 px-5.5 py-4 whitespace-normal">
                          <Link
                            to={`/admin/contenidos/plantillas/${plantilla.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-ink text-[0.9375rem] font-bold hover:underline"
                          >
                            {plantilla.nombre}
                          </Link>
                          <p className="text-ink-hint mt-1 text-[0.71875rem] font-semibold">
                            {respaldoDeLaPlantilla(plantilla.informes_analizados)}
                          </p>
                          {plantilla.proposito ? (
                            <p className="text-ink-muted mt-1 line-clamp-2 text-[0.78125rem] leading-[1.45] font-medium">
                              {plantilla.proposito}
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-ink text-right font-mono text-[0.9375rem] font-bold">
                          {plantilla.total}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1.5">
                            <BarraDeComposicion
                              obligatorios={plantilla.obligatorios}
                              condicionales={plantilla.condicionales}
                              opcionales={plantilla.opcionales}
                            />
                            <ConteoDeObligatoriedad
                              obligatorios={plantilla.obligatorios}
                              condicionales={plantilla.condicionales}
                              opcionales={plantilla.opcionales}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-ink-ghost pr-5">
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </div>

      <NuevaPlantillaDialog
        abierto={creando}
        plantillas={lista}
        onCerrar={() => setCreando(false)}
        onCreada={(id) => {
          setCreando(false);
          navigate(`/admin/contenidos/plantillas/${id}`);
        }}
      />
    </div>
  );
}
